import React from 'react';
import PropTypes from 'prop-types';
import {
  GridContainer,
  Column,
  Row,
  Typography,
  Box,
  FlexBox,
  Link,
  H2,
  Button,
} from '@vp/swan';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import { CouponPanel } from '../coupon/CouponPanel';
import { Spacer } from '../shared/spacer';
import useSiteData from '../../hooks/useSiteData';
import useIdentityContext from '../../hooks/useIdentityContext';
import ContinueShopping from '../layout/layoutComponents/ContinueShopping';
import { EMPTY_CART_SIGN_IN_CTA_BUTTON } from '../../constants/authTracking';
import { FindOrderIcon } from '../../../assets/icons/FindOrderIcon';
import useTracking from '../../hooks/useTracking';

/**
 * Box to display coupon entry on the empty cart screen
 */
const CouponEntryBox = () => {
  const [couponMessages] = useTranslations(['couponMessages']);
  return (
    <FlexBox marginBottom={9} marginTop={4}>
      <Spacer size="l" hiddenXS />
      <FlexBox
        marginBottom={6}
        justifyContent="center"
        alignItems="flex-start"
        flexDirection="column"
      >
        <H2 fontSize={3} marginBottom={4}>
          {couponMessages.enterPromoCode}
        </H2>
        <CouponPanel />
      </FlexBox>
    </FlexBox>
  );
};

/**
 * Sign In Button in the empty cart page
 */
const SignInButton = ({ children }) => {
  const { auth } = useIdentityContext();

  return (
    <Button
      skin="secondary"
      onClick={() =>
        auth.signIn([], [], { trackingCaller: EMPTY_CART_SIGN_IN_CTA_BUTTON })
      }
    >
      {children}
    </Button>
  );
};
SignInButton.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

/**
 * Box to display sign in or order tracking on empty cart page
 */
const SignInBox = () => {
  const [trackOrderMessages] = useTranslations(['trackOrderMessages']);
  const { trackNavigationClicked } = useTracking();
  const { externalAppNavigation } = useSiteData();
  const { identity } = useIdentityContext();
  const { isAnonymousUser } = identity;

  let title;
  let subtitle;
  let cta;
  let link;

  if (isAnonymousUser) {
    title = trackOrderMessages.signIn;
    subtitle = trackOrderMessages.signInMessage;
    cta = <SignInButton>{trackOrderMessages.signIn}</SignInButton>;
    link = externalAppNavigation.orderDetails;
  } else {
    title = trackOrderMessages.trackYourOrder;
    subtitle = null;
    cta = null;
    link = externalAppNavigation.orderHistory;
  }

  return (
    <FlexBox marginTop={4}>
      <Spacer size="l" hiddenXS />
      <Box>
        <H2 fontSize={3} marginBottom={4}>
          {title}
        </H2>

        {subtitle && <Typography marginY={4}>{subtitle}</Typography>}

        <FlexBox alignItems="center" style={{ columnGap: '1em' }}>
          {cta}
          <Link
            onClick={() => {
              trackNavigationClicked({
                sourcePagePath: externalAppNavigation.cart,
                destinationPagePath: link,
                ctaValue: 'emptyCartFindAndTrackOrder',
              });
            }}
            withIcon
            skin="unstyled"
            href={link}
            width="standard"
          >
            <FindOrderIcon />

            <Typography paddingX={4} fontWeight="bold">
              {trackOrderMessages.findAndTrackOrder}
            </Typography>
          </Link>
        </FlexBox>
      </Box>
    </FlexBox>
  );
};

// MyProjects link and text
const MyProjectsLink = ({ children }) => {
  const { externalAppNavigation } = useSiteData();
  const { trackNavigationClicked } = useTracking();
  const myProjectsUrl = externalAppNavigation.myProjects;
  return (
    <Link
      skin="cta"
      onClick={() => {
        trackNavigationClicked({
          sourcePagePath: externalAppNavigation.cart,
          destinationPagePath: externalAppNavigation.orderHistory,
          ctaValue: 'emptyCartMyProjects',
        });
      }}
      href={myProjectsUrl}
    >
      {children}
    </Link>
  );
};
MyProjectsLink.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

// OrderHistory link and text
const OrderHistoryLink = ({ children }) => {
  const { externalAppNavigation } = useSiteData();
  const { trackNavigationClicked } = useTracking();
  const orderHistoryUrl = externalAppNavigation.orderHistory;
  return (
    <Link
      skin="cta"
      onClick={() => {
        trackNavigationClicked({
          sourcePagePath: externalAppNavigation.cart,
          destinationPagePath: externalAppNavigation.orderHistory,
          ctaValue: 'emptyCartOrderHistory',
        });
      }}
      href={orderHistoryUrl}
    >
      {children}
    </Link>
  );
};

OrderHistoryLink.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

/**
 * Box to link user to projects, orders, or to continue shopping
 */
const ContinueShoppingBox = () => {
  const { externalAppNavigation } = useSiteData();
  const [cartMessages] = useTranslations(['cartMessages']);
  const { identity } = useIdentityContext();
  const { isAnonymousUser } = identity;

  const myProjectsUrl = externalAppNavigation.myProjects;
  const orderHistoryUrl = externalAppNavigation.orderHistory;

  let emptyCartMessageCopy = cartMessages.baseEmptyCartMessage;
  // If the user is signed in and their cart is empty, offer to take them to my projects / order history (if available)
  if (myProjectsUrl && orderHistoryUrl && !isAnonymousUser) {
    emptyCartMessageCopy = cartMessages.emptyCartMessage;
  }

  return (
    <>
      <Box marginTop={4}>
        <ReplaceTokens
          tokenizedString={emptyCartMessageCopy}
          substitutions={{
            MyProjectsLink,
            OrderHistoryLink,
          }}
        />
        <Spacer size="l" hiddenXS />
      </Box>
      <Row marginBottom={6}>
        <ContinueShopping buttonText={cartMessages.shopNow} />
      </Row>
    </>
  );
};

/**
 * Assumes there are no cart items.  Just display message and other site links.
 */
export const EmptyCartDisplay = () => {
  const { noCouponEntry } = useSiteData();

  const nodes = [
    {
      key: 'continueShopping',
      value: <ContinueShoppingBox />,
    },
    { key: 'couponEntry', value: !noCouponEntry && <CouponEntryBox /> },
    { key: 'signIn', value: <SignInBox /> },
  ].filter(n => !!n.value);

  // evenly split total span of 12 between all nodes (if possible)
  // do not give any node more than 6 span
  const span = Math.min(Math.floor(12 / nodes.length), 6);

  return (
    <GridContainer dividers>
      <Row>
        {nodes.map(n => (
          <Column key={n.key} span={span}>
            {n.value}
          </Column>
        ))}
      </Row>
    </GridContainer>
  );
};
